import { showError } from "nsitools-react";
import * as React from "react";
import { useHistory, useParams } from "react-router";
import { SuiviWalterForm } from "../..";

import { EFormePersonne, SuiviApprenantApi } from "../../../../api";
import { ERoutes } from "../../../../AppRouter";
import { useDialog } from "../../../../contexts";
import { useApiService, useCrudApi, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { exportFile } from "../../../../utils";

export interface ISuiviWalterDetailPageProps {
  baseRoute: string;
}

export const SuiviWalterDetailPage: React.FunctionComponent<ISuiviWalterDetailPageProps> = ({ baseRoute }) => {
  const { t } = useTl();
  const api = useApiService(SuiviApprenantApi);
  const { id, state, tab } = useParams<{
    id: string;
    tab: string;
    state: string;
  }>();
  const editMode = React.useMemo(() => state === "edit", [state]);
  const IdSuivi = React.useMemo(() => +id, [id]);
  const history = useHistory();

  const { data, loading, deleteItem, deleting, saveItem, saving, validationErrors, refresh } = useCrudApi({
    getApiFn: () => api.suiviApprenantGet({ id: IdSuivi }),
    saveApiFn: d => api.suiviApprenantSave({ FcbSuiviApprenantDetailsDto: d }),
    onSavedRoute: d => `${ERoutes.suivi}/${d.idsuivi}/${tab}/${state}/walter`,
    deleteApiFn: d => api.suiviApprenantDelete({ id: d.idsuivi }),
    onDeletedRoute: d => `${ERoutes.suivi}`
  });

  const onCancel = React.useCallback(() => history.push(`${baseRoute}/${id}/${tab}/${state}`), [
    baseRoute,
    history,
    id,
    state,
    tab
  ]);

  const [rowLoading, setRowLoading] = React.useState(null);
  const { showDialogPromise } = useDialog();
  const deleteFichier = React.useCallback(
    async (idsuiviFichier: number) => {
      const result = await showDialogPromise({
        message: t(ETLCodes.DeleteConfirmationMessage)
      });

      if (result === "yes") {
        try {
          setRowLoading(idsuiviFichier);
          await api.suiviApprenantDeleteSuiviApprenantFichier({
            id: idsuiviFichier
          });
          refresh();
        } catch {
          showError(t(ETLCodes.ErrorWhileDeletingFile));
        } finally {
          setRowLoading(null);
        }
      }
    },
    [api, refresh, showDialogPromise, t]
  );

  const downloadFichier = React.useCallback(
    async (idsuiviFichier: number) => {
      try {
        setRowLoading(idsuiviFichier);
        const file = await api.suiviApprenantDownloadSuiviApprenantFichier({
          id: idsuiviFichier
        });
        await exportFile(file);
      } catch {
        showError(t(ETLCodes.ErrorWhileDownloadingFile));
      } finally {
        setRowLoading(null);
      }
    },
    [api, t]
  );

  const forme = React.useMemo(() => {
    if (data?.idapprenant) return EFormePersonne.Apprenant;
    if (data?.idformateur) return EFormePersonne.Formateur;
  }, [data?.idapprenant, data?.idformateur]);

  return (
    <SuiviWalterForm
      data={data}
      suiviId={+IdSuivi}
      saveItem={saveItem}
      loading={loading}
      saving={saving}
      editMode={editMode && !data?.fromSmile}
      deleteItem={deleteItem}
      deleting={deleting}
      onCancel={onCancel}
      validationErrors={validationErrors}
      fieldsetTitle={t(ETLCodes.Suivi)}
      deleteFichier={deleteFichier}
      downloadFichier={downloadFichier}
      rowLoading={rowLoading}
      refreshData={refresh}
      forme={forme}
    />
  );
};
